/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {AssetSummaryCardIdentity} from './AssetSummaryCardIdentity';
import {AssetSummaryCardStatus, AssetSummaryCardStatusProps} from './AssetSummaryCardStatus';

interface AssetSummaryCardHeaderProps {
  assetType: string;
  assetName: string;
  alias?: string;
  onAssetNameClick?: () => void;
  status: AssetSummaryCardStatusProps;
}

const useStyles = makeStyles(() => ({
  header : {
    display    : 'flex',
    width      : '100%',
    minWidth   : 0,
    minHeight  : '44px',
    alignItems : 'flex-start',
    gap        : '16px'
  }
}));

/**
 * AssetSummaryCardHeader combines Header Left (Asset Identity) and Header
 * Right (Asset Status), per ION-7393: at least 16px between the two
 * sections, and a height that grows to fit whichever side wraps or has
 * fewer rows - a plain flex row already gives both for free.
 *
 * ```typescript
 *
 *   import {AssetSummaryCardHeader} from
 *     '@ion/dls/src/components/assetcard/current/AssetSummaryCardHeader';
 *   ...
 *
 *   <AssetSummaryCardHeader assetType={AssetType.Vehicle}
 *     assetName="Vehicle 13 Vision"
 *     alias="004013982 - HUMAN SERVICES - 21-0763"
 *     status={{
 *       stateLabel: 'Moving (no Ignition)',
 *       stateTextColor: dlsColor('paleOrange'),
 *       stateBackgroundColor: 'rgba(225, 114, 0, 0.1)',
 *       dateTime: '12/16/2025 - 11:08 AM CST',
 *       speed: '11 MPH'
 *     }} />
 *
 * ```
 *
 */
export const AssetSummaryCardHeader = ({
  assetType,
  assetName,
  alias,
  onAssetNameClick,
  status
}: AssetSummaryCardHeaderProps) => {
  const classes = useStyles();
  return (
    <div className={classes.header}>
      <AssetSummaryCardIdentity assetType={assetType}
        assetName={assetName}
        alias={alias}
        onAssetNameClick={onAssetNameClick} />
      <AssetSummaryCardStatus {...status} />
    </div>
  );
};

export default AssetSummaryCardHeader;
